import { Smile, Meh, Frown } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

interface MoodSelectorProps {
  value: number;
  onChange: (value: number) => void;
  label?: string;
  error?: string;
}

const moodOptions = [
  { value: 1, emoji: '😞', label: 'Muito Baixo', color: 'text-red-500', Icon: Frown },
  { value: 2, emoji: '😕', label: 'Baixo', color: 'text-orange-500', Icon: Frown },
  { value: 3, emoji: '😐', label: 'Neutro', color: 'text-yellow-500', Icon: Meh },
  { value: 4, emoji: '🙂', label: 'Bom', color: 'text-green-500', Icon: Smile },
  { value: 5, emoji: '😄', label: 'Excelente', color: 'text-emerald-500', Icon: Smile },
];

/**
 * Componente para selecionar humor/mood (1-5)
 */
export function MoodSelector({ value, onChange, label, error }: MoodSelectorProps) {
  return (
    <div className="space-y-2">
      {label && <label className="text-sm font-medium text-foreground">{label}</label>}

      <div className="flex gap-2 flex-wrap">
        {moodOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              'flex flex-col items-center p-3 rounded-lg border-2 transition-all hover:scale-105',
              value === option.value
                ? 'border-primary bg-primary/10'
                : 'border-input hover:border-ring'
            )}
          >
            <span className="text-3xl mb-1">{option.emoji}</span>
            <span className="text-xs font-medium text-foreground">{option.label}</span>
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

/**
 * Badge de mood para exibir o valor
 */
export function MoodBadge({ value }: { value: number }) {
  const option = moodOptions.find((o) => o.value === value);

  if (!option) return null;

  return (
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted">
      <span className="text-lg">{option.emoji}</span>
      <span className="text-sm font-medium text-foreground">{option.label}</span>
    </div>
  );
}
